import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Shell } from '@/components/layout/Shell';
import { OfflineBar } from '@/components/layout/OfflineBar';
import { LockScreen } from '@/components/auth/LockScreen';
import { UpdateBanner } from '@/components/update/UpdateBanner';
import { ForceUpdateScreen } from '@/components/update/ForceUpdateScreen';
import { LoginPage } from '@/features/auth/LoginPage';
import { OrdersPage } from '@/features/orders/OrdersPage';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { useMe } from '@/hooks/useMe';
import { useInstallPromptCapture } from '@/hooks/useInstallPrompt';
import { useAppBadge } from '@/hooks/useAppBadge';
import { useNewOrderAlarm } from '@/hooks/useNewOrderAlarm';
import { usePushSubscription } from '@/hooks/usePushSubscription';
import { useIdleLock } from '@/hooks/useIdleLock';
import { useOrientationLock } from '@/hooks/useOrientationLock';
import { useLockStore } from '@/stores/lockStore';
import { useUpdateStore } from '@/stores/updateStore';
import { setUnauthorizedHandler } from '@/lib/apiClient';

// Routes that aren't part of the default landing path get split into their own
// chunks so the orders board can boot fast. /pickup pulls in @zxing/browser; the
// other two are smaller but still worth deferring.
const PickupPage = lazy(() =>
  import('@/features/pickup/PickupPage').then((m) => ({ default: m.PickupPage })),
);
const HistoryPage = lazy(() =>
  import('@/features/orders/HistoryPage').then((m) => ({ default: m.HistoryPage })),
);
const TablesPage = lazy(() =>
  import('@/features/tables/TablesPage').then((m) => ({ default: m.TablesPage })),
);
const MenuPage = lazy(() =>
  import('@/features/menu/MenuPage').then((m) => ({ default: m.MenuPage })),
);
const SettingsPage = lazy(() =>
  import('@/features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);

function PageFallback() {
  return (
    <div className="flex h-full min-h-[40vh] items-center justify-center text-sm text-muted-foreground">
      …
    </div>
  );
}

function ProtectedShell() {
  const status = useAuthStore((s) => s.status);
  const location = useLocation();

  if (status === 'idle' || status === 'loading') {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (status === 'unauthenticated') {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  return <Shell />;
}

function AuthenticatedRedirect({ children }: { children: React.ReactNode }) {
  const status = useAuthStore((s) => s.status);
  if (status === 'authenticated') return <Navigate to="/orders" replace />;
  return <>{children}</>;
}

function ThemeApplier() {
  const theme = useUIStore((s) => s.theme);
  useEffect(() => {
    const root = document.documentElement;
    const apply = (mode: 'light' | 'dark') => {
      root.classList.toggle('dark', mode === 'dark');
    };
    if (theme === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      apply(mq.matches ? 'dark' : 'light');
      const handler = (e: MediaQueryListEvent) => apply(e.matches ? 'dark' : 'light');
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    apply(theme);
  }, [theme]);
  return null;
}

function LangApplier() {
  const language = useUIStore((s) => s.language);
  const { i18n } = useTranslation();
  useEffect(() => {
    if (i18n.language !== language) void i18n.changeLanguage(language);
  }, [i18n, language]);
  return null;
}

function UnauthorizedRouter() {
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  useEffect(() => {
    setUnauthorizedHandler(() => {
      logout();
      navigate('/login', { replace: true });
    });
  }, [logout, navigate]);
  return null;
}

export function App() {
  useMe();
  useInstallPromptCapture();
  useAppBadge();
  useNewOrderAlarm();
  usePushSubscription();
  useIdleLock();
  const orientation = useUIStore((s) => s.orientation);
  useOrientationLock(orientation);

  const isLocked = useLockStore((s) => s.isLocked);
  const credentialId = useLockStore((s) => s.credentialId);
  const isAuthenticated = useAuthStore((s) => s.status === 'authenticated');
  const lockActive = isAuthenticated && isLocked && Boolean(credentialId);
  const isForceUpdate = useUpdateStore((s) => s.isForceUpdate);

  return (
    <>
      <ThemeApplier />
      <LangApplier />
      <UnauthorizedRouter />
      <OfflineBar />
      <UpdateBanner />
      {lockActive && <LockScreen />}
      {isForceUpdate && <ForceUpdateScreen />}
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route
            path="/login"
            element={
              <AuthenticatedRedirect>
                <LoginPage />
              </AuthenticatedRedirect>
            }
          />

          <Route element={<ProtectedShell />}>
            <Route index element={<Navigate to="/orders" replace />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/orders/:id" element={<OrdersPage />} />
            <Route path="/pickup" element={<PickupPage />} />
            <Route path="/tables" element={<TablesPage />} />
            <Route path="/menu" element={<MenuPage />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="*" element={<Outlet />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  );
}
